import { createContext, useCallback, useContext, useLayoutEffect, useId, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { registerSetupPopHandler } from "./setupNavigationGuard";

export const SetupDraftContext = createContext(null);

export function useEditorDraft(values, initial) {
  const context = useContext(SetupDraftContext);
  const id = useId();
  const initialValues = useRef(JSON.stringify(initial));
  const dirty = JSON.stringify(values) !== initialValues.current;
  const update = context?.update;
  useLayoutEffect(() => {
    update?.(id, dirty);
    return () => update?.(id, false);
  }, [update, id, dirty]);
  return action => context ? context.submit(id, action) : action();
}

export default function useSetupDrafts({ message = "Save your changes in this step before continuing, or discard the unsaved edits to continue.", actionClass, secondaryClass } = {}) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const entries = useRef(new Set());
  const submitting = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(null);
  const [reset, setReset] = useState(0);
  const update = useCallback((id, changed) => {
    if (changed) entries.current.add(id); else entries.current.delete(id);
    setDirty(entries.current.size > 0);
  }, []);
  const guard = useCallback(action => {
    if (entries.current.size) setPending(() => action); else action();
  }, []);
  const submit = useCallback((id, action) => {
    const save = async () => { submitting.current = true; try { return await action(); } finally { submitting.current = false; } };
    if ([...entries.current].some(key => key !== id)) setPending(() => save);
    else return save();
  }, []);
  const saved = useCallback(() => {
    entries.current.clear(); setDirty(false); setPending(null);
  }, []);
  useLayoutEffect(() => {
    if (!dirty) return undefined;
    const unload = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", unload);
    return () => window.removeEventListener("beforeunload", unload);
  }, [dirty]);
  // BrowserRouter does not expose the data-router blocker. Intercept its
  // navigation methods locally while this setup has edits, including toolbar
  // buttons that navigate without an anchor. Restore every method on cleanup.
  useLayoutEffect(() => {
    if (!dirty) return undefined;
    const original = Object.fromEntries(["push", "replace", "go"].map(key => [key, navigator[key]]));
    const wrappers = Object.fromEntries(Object.entries(original).map(([key, method]) => [key, (...args) => {
      if (!entries.current.size) return method.apply(navigator, args);
      guard(() => method.apply(navigator, args));
    }]));
    Object.assign(navigator, wrappers);
    return () => Object.keys(original).forEach(key => { if (navigator[key] === wrappers[key]) navigator[key] = original[key]; });
  }, [dirty, guard, navigator]);
  useLayoutEffect(() => {
    if (!dirty) return undefined;
    const index = window.history.state?.idx;
    if (!Number.isInteger(index)) return undefined;
    let restore = null;
    const pop = event => {
      if (!entries.current.size || !Number.isInteger(event.state?.idx)) return;
      if (restore && event.state.idx === index) {
        event.stopImmediatePropagation();
        const action = restore; restore = null; guard(action);
      } else if (event.state.idx !== index) {
        event.stopImmediatePropagation();
        const delta = event.state.idx - index;
        restore = () => window.history.go(delta);
        window.history.go(-delta);
      }
    };
    return registerSetupPopHandler(pop);
  }, [dirty, guard]);
  const discard = () => {
    const action = pending;
    entries.current.clear(); setDirty(false); setReset(value => value + 1); setPending(null);
    action?.();
  };
  const dialog = <Dialog open={!!pending} onClose={() => setPending(null)} className="fixed inset-0 z-50">
    <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-4"><DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      <DialogTitle className="text-lg font-semibold text-slate-900">You have unsaved changes</DialogTitle>
      <p className="mt-2 text-sm text-slate-600">{message}</p>
      <div className="mt-6 flex flex-wrap justify-end gap-3"><button onClick={() => setPending(null)} className={secondaryClass || "min-h-[44px] rounded-lg border px-4 py-2 text-sm font-semibold"} autoFocus>Keep editing</button><button onClick={discard} className={actionClass || "min-h-[44px] rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white"}>Discard & continue</button></div>
    </DialogPanel></div>
  </Dialog>;
  return { guard, reset, dirty, submitting, saved, context: { update, submit }, dialog };
}
